import { PrimaryButton } from '@components/Buttons/PrimaryButton';
import { GridItem } from '@components/GridItem';
import { HorizontalGrid } from '@components/HorizontalGrid';
import { Main } from '@components/Main';
import { PageContent } from '@components/PageContent';
import { PageTitle, PageTitleText } from '@components/PageTitle';
import { useData } from '@hooks/useData';
import { useEffectOnce } from '@hooks/useEffectOnce';
import { useField } from '@hooks/useField';
import useLoading from '@hooks/useLoading';
import { useMenu } from '@hooks/useMenu';
import useMessage from '@hooks/useMessage';
import { useModalWindow } from '@hooks/useModalWindow';
import { EnumSortDirection } from '@hooks/useRequest';
import { Box, makeStyles } from '@material-ui/core';
import { EnumVendedorStatus, IVendedor, useVendedorStatusCombo } from '@model/Vendedor';
import { IVendedorFilter, useVendedorService } from '@services/VendedorService';
import clsx from 'clsx';
import React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { VendedorFicha } from './Ficha';
import { VendedorFilter } from './VendedorFilter';
import { VendedorMenu } from './VendedorMenu';
import { VendedorRegistrarModal } from './VendedorRegistrarModal';
import { VendedorTable } from './VendedorTable';

export type VendedorParams = {
    id: string
}

export type VendedorProps = {
}

export function Vendedor (props: VendedorProps) {
    const data = useData<IVendedor, IVendedorFilter>({
        filter: {
            vendedorId: useField(),
            nome: useField(),
            cpf: useField(),
            status: useField(EnumVendedorStatus.Ativo)
        },

        sortField: {
            name: "nome",
            direction: EnumSortDirection.Asc
        }
    })

    const params = useParams<VendedorParams>();
    const vendedorId = React.useMemo(() => params.id !== undefined ? parseInt(params.id) : undefined, [params.id]);

    const cls = useStyles();

    const loading = useLoading();
    const message = useMessage();
    const vendedorService = useVendedorService();
    const menu = useMenu();
    const vendedorStatusCombo = useVendedorStatusCombo();

    const history = useHistory();

    const vendedorRegistrarModal = useModalWindow();

    const handleVendedorAtualizado = React.useCallback(() => {
        history.push("/vendedor");        
    }, [history]);

    React.useEffect(() => {
        if (!loading.isLoading) {
            loading.start();
            vendedorService.listar(data.requestedData)
                .then(data.handleResultResponse)
                .catch(message.showException)
                .finally(loading.stop);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data.requestedData]);

    useEffectOnce(() => {
        vendedorStatusCombo.loadOptions();
    })

    return (
        <React.Fragment>
            <Box className={clsx({
                [cls.hidden]: vendedorId !== undefined
            })}>
                <Main>
                    <PageTitle>
                        <HorizontalGrid>
                            <GridItem>
                                <PageTitleText text="Vendedores" />
                            </GridItem>
                            <GridItem floatRight>
                                <PrimaryButton onClick={vendedorRegistrarModal.open}>Novo Vendedor</PrimaryButton>
                            </GridItem>
                        </HorizontalGrid>
                    </PageTitle>

                    <PageContent>
                        <VendedorFilter
                            filter={data.filter}
                            isLoading={loading.isLoading}
                            vendedorStatusCombo={vendedorStatusCombo}
                        />
                        
                        <VendedorTable 
                            data={data}
                            isLoading={loading.isLoading}
                            menu={menu}
                        />
                    </PageContent>

                    {
                        data.focusedRow !== undefined && (
                            <React.Fragment>
                                <VendedorMenu 
                                    menu={menu}
                                    commandLoading={loading}
                                    onVendedorAtualizado={data.handleUpdateFocusedRow}
                                    vendedor={data.focusedRow}
                                />
                            </React.Fragment>
                        )
                    }

                    <VendedorRegistrarModal 
                        modal={vendedorRegistrarModal}
                        onVendedorRegistrado={data.filter.handleFilterRequest}
                    />
                </Main>
            </Box>

            <Box className={clsx({
                [cls.hidden]: vendedorId === undefined
            })}>
                    <VendedorFicha
                        vendedorId={vendedorId}
                        onVendedorAtualizado={handleVendedorAtualizado}
                    />
            </Box>            
        </React.Fragment>
    )
}

const useStyles = makeStyles(theme => ({
    hidden: {
        display: 'none'
    }
}))